import React, { memo, useCallback, useMemo, useState } from 'react';

import { Box, Button, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { RadioGroupCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';

import { AI_QUESTION_CARD_LABELS, QUESTION_TYPE } from './AiQuestionCard.constants';
import { aiQuestionCardPropTypes } from './AiQuestionCard.propTypes';
import {
  cardContainerStyles,
  footerSpacerStyles,
  footerStyles,
  optionDescriptionStyles,
  optionLabelStyles,
  optionsContainerStyles,
  progressBarStyles,
  progressContainerStyles,
  progressTextStyles,
  questionContainerStyles,
  questionHeaderStyles,
  questionTextStyles,
  trailContainerStyles,
  trailTitleStyles,
} from './AiQuestionCard.styles';
import { AiQuestionCardTestIds } from './AiQuestionCard.testIds';
import {
  buildAnswersJson,
  getAnswerSummary,
  getProgressLabel,
  getProgressValue,
  getQuestionType,
  hasAnswer,
} from './AiQuestionCard.utils';
import AnsweredTrailItem from './components/AnsweredTrailItem';
import QuestionMultiSelectOption from './components/QuestionMultiSelectOption';

const AiQuestionCard = ({ questions, onSubmit, onDismiss, isSubmitting = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const total = questions.length;
  const currentQuestion = questions[currentIndex];
  const currentHeader = currentQuestion?.header;
  const currentType = useMemo(() => getQuestionType(currentQuestion), [currentQuestion]);
  const currentValue = answers[currentHeader];
  const isLastStep = currentIndex === total - 1;
  const isCurrentAnswered = hasAnswer(currentValue);
  const isControlsDisabled = isSubmitting;

  const radioOptions = useMemo(
    () =>
      currentType === QUESTION_TYPE.SINGLE_SELECT
        ? currentQuestion.options.map(option => ({
            value: option.label,
            label: (
              <Box sx={optionLabelStyles}>
                <Typography variant="regular14">{option.label}</Typography>
                {option.description ? (
                  <Typography sx={optionDescriptionStyles} variant="regular12">
                    {option.description}
                  </Typography>
                ) : null}
              </Box>
            ),
          }))
        : [],
    [currentQuestion, currentType],
  );

  const selectedMultiValues = useMemo(
    () => (_.isArray(currentValue) ? currentValue : []),
    [currentValue],
  );

  const trailItems = useMemo(
    () =>
      questions.slice(0, currentIndex).map((question, index) => ({
        index,
        header: question.header,
        summary: getAnswerSummary(answers[question.header]),
      })),
    [answers, currentIndex, questions],
  );

  const submit = useCallback(
    finalAnswers => {
      onSubmit(buildAnswersJson(questions, finalAnswers));
    },
    [onSubmit, questions],
  );

  const onSelectSingle = useCallback(
    event => {
      const { value } = event.target;

      setAnswers(previous => ({ ...previous, [currentHeader]: value }));
    },
    [currentHeader],
  );

  const onToggleMulti = useCallback(
    (label, checked) => {
      setAnswers(previous => {
        const selected = previous[currentHeader] ?? [];
        const nextSelected = checked
          ? _.union(selected, [label])
          : selected.filter(item => item !== label);

        return { ...previous, [currentHeader]: nextSelected };
      });
    },
    [currentHeader],
  );

  const onChangeText = useCallback(
    event => {
      const { value } = event.target;

      setAnswers(previous => ({ ...previous, [currentHeader]: value }));
    },
    [currentHeader],
  );

  const onBack = useCallback(() => {
    setCurrentIndex(previous => Math.max(0, previous - 1));
  }, []);

  const onEdit = useCallback(index => {
    setCurrentIndex(index);
  }, []);

  const onContinue = useCallback(() => {
    if (isLastStep) {
      submit(answers);

      return;
    }

    setCurrentIndex(previous => previous + 1);
  }, [answers, isLastStep, submit]);

  const onSkip = useCallback(() => {
    const nextAnswers = _.omit(answers, currentHeader);

    setAnswers(nextAnswers);

    if (isLastStep) {
      submit(nextAnswers);

      return;
    }

    setCurrentIndex(previous => previous + 1);
  }, [answers, currentHeader, isLastStep, submit]);

  if (total === 0 || _.isNil(currentQuestion)) {
    return null;
  }

  return (
    <Box
      aria-label="Ask the user a question"
      data-testid={AiQuestionCardTestIds.WRAPPER}
      role="group"
      sx={cardContainerStyles}
    >
      <Box sx={progressContainerStyles}>
        <Typography
          data-testid={AiQuestionCardTestIds.PROGRESS_TEXT}
          sx={progressTextStyles}
          variant="medium12"
        >
          {getProgressLabel(currentIndex, total)}
        </Typography>
        <LinearProgress
          aria-valuenow={currentIndex + 1}
          data-testid={AiQuestionCardTestIds.PROGRESS_BAR}
          sx={progressBarStyles}
          value={getProgressValue(currentIndex, total)}
          variant="determinate"
        />
      </Box>

      {trailItems.length > 0 ? (
        <Box data-testid={AiQuestionCardTestIds.ANSWERED_TRAIL} sx={trailContainerStyles}>
          <Typography sx={trailTitleStyles} variant="medium12">
            {AI_QUESTION_CARD_LABELS.ANSWERED_TRAIL_TITLE}
          </Typography>
          {trailItems.map(item => (
            <AnsweredTrailItem
              disabled={isControlsDisabled}
              header={item.header}
              index={item.index}
              key={item.header}
              summary={item.summary}
              onEdit={onEdit}
            />
          ))}
        </Box>
      ) : null}

      <Box sx={questionContainerStyles}>
        <Typography
          data-testid={AiQuestionCardTestIds.QUESTION_HEADER}
          sx={questionHeaderStyles}
          variant="medium14"
        >
          {currentQuestion.header}
        </Typography>
        <Typography
          data-testid={AiQuestionCardTestIds.QUESTION_TEXT}
          sx={questionTextStyles}
          variant="regular14"
        >
          {currentQuestion.question}
        </Typography>

        {currentType === QUESTION_TYPE.SINGLE_SELECT ? (
          <RadioGroupCDS
            dataTestId={AiQuestionCardTestIds.SINGLE_SELECT}
            disabled={isControlsDisabled}
            name={currentHeader}
            options={radioOptions}
            value={currentValue ?? ''}
            onChange={onSelectSingle}
          />
        ) : null}

        {currentType === QUESTION_TYPE.MULTI_SELECT ? (
          <Box sx={optionsContainerStyles}>
            {currentQuestion.options.map(option => (
              <QuestionMultiSelectOption
                checked={selectedMultiValues.includes(option.label)}
                description={option.description}
                disabled={isControlsDisabled}
                key={option.label}
                label={option.label}
                onToggle={onToggleMulti}
              />
            ))}
          </Box>
        ) : null}

        {currentType === QUESTION_TYPE.FREE_TEXT ? (
          <TextFieldCDS
            fullWidth
            multiline
            data-testid={AiQuestionCardTestIds.FREE_TEXT_INPUT}
            disabled={isControlsDisabled}
            minRows={2}
            name={currentHeader}
            placeholder={AI_QUESTION_CARD_LABELS.FREE_TEXT_PLACEHOLDER}
            size="small"
            value={currentValue ?? ''}
            onChange={onChangeText}
          />
        ) : null}
      </Box>

      <Box sx={footerStyles}>
        {currentIndex > 0 ? (
          <Button
            data-testid={AiQuestionCardTestIds.BACK_BUTTON}
            disabled={isControlsDisabled}
            size="small"
            variant="line"
            onClick={onBack}
          >
            {AI_QUESTION_CARD_LABELS.BACK}
          </Button>
        ) : null}
        <Button
          data-testid={AiQuestionCardTestIds.DISMISS_BUTTON}
          disabled={isControlsDisabled}
          size="small"
          variant="text"
          onClick={onDismiss}
        >
          {AI_QUESTION_CARD_LABELS.DISMISS}
        </Button>
        <Box sx={footerSpacerStyles} />
        {!isCurrentAnswered ? (
          <Button
            data-testid={AiQuestionCardTestIds.SKIP_BUTTON}
            disabled={isControlsDisabled}
            size="small"
            variant="line"
            onClick={onSkip}
          >
            {AI_QUESTION_CARD_LABELS.SKIP}
          </Button>
        ) : null}
        <Button
          data-testid={AiQuestionCardTestIds.CONTINUE_BUTTON}
          disabled={isControlsDisabled || !isCurrentAnswered}
          size="small"
          onClick={onContinue}
        >
          {isLastStep ? AI_QUESTION_CARD_LABELS.SUBMIT : AI_QUESTION_CARD_LABELS.CONTINUE}
        </Button>
        {isSubmitting ? <CircularProgress size={14} thickness={4} /> : null}
      </Box>
    </Box>
  );
};

AiQuestionCard.propTypes = aiQuestionCardPropTypes;

export default memo(AiQuestionCard);
